import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import pick from 'lodash/pick';
import { translateError } from '../../cognito/cognito-service';
import { login, loginNaviance } from '../Authorization/actions';
import List from '../../components/List';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import Container from '../../components/Container';
import Loader from '../../components/Loader';
import Dimmer from '../../components/Dimmer';
import Segment from '../../components/Segment';
import Form from '../../components/Form';
import Label from '../../components/Label';
import Message from '../../components/Message';

import { isMockingNavianceLogin } from '../../utils';

import './styles.css';

const missingValueLabel = (
  <Label role="tooltip" basic color="red" pointing data-cy="login-please-enter-value-message-tooltip">
    <FormattedMessage id="login-page.missingValue" defaultMessage="Please enter a value" />
  </Label>
);

const messages = defineMessages({
  loggingIn: {
    id: 'login-page.loggingIn',
    defaultMessage: 'Logging In...',
  },
  username: {
    id: 'login-page.placeholder.username',
    defaultMessage: 'E-Mail Address',
  },
  password: {
    id: 'login-page.placeholder.password',
    defaultMessage: 'Password',
  },
});

function validateForm(formData) {
  const newState = {
    missingName: null,
    missingPassword: null,
  };
  if (formData.username.length === 0) {
    newState.missingName = missingValueLabel;
  }
  if (formData.password.length === 0) {
    newState.missingPassword = missingValueLabel;
  }
  return newState;
}

class LoginPage extends Component {
  static propTypes = {
    current: PropTypes.object.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    login: PropTypes.func.isRequired,
    loginNaviance: PropTypes.func.isRequired,
  };

  state = {
    username: '', password: '', requesting: false, errorCode: '', missingName: false, missingPw: false,
  };

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ current }) {
    const { errorCode } = current.viewer;
    if (errorCode !== this.state.errorCode) {
      this.setState({
        errorCode,
        requesting: false,
      });
    }
  }

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    const formData = pick(this.state, 'username', 'password');
    e.preventDefault();
    const newState = validateForm(formData);
    if (!Object.values(newState).some((value) => value)) {
      this.props.login(formData);
      newState.requesting = true;
      newState.errorCode = null;
    }
    this.setState(newState);
  };

  _onNavianceLogin = (e) => {
    this.props.loginNaviance();
  }

  render() {
    const { intl } = this.props;
    const { errorCode, missingName, missingPassword, requesting, username, password } = this.state;
    const errorDetails = errorCode ? (
      <Message role="alert" negative data-cy="login-error-message-tooltip">
        <Message.Header><FormattedMessage {...translateError(errorCode)} /></Message.Header>
      </Message>
    ) : '';
    const _isMockingNavianceLogin = isMockingNavianceLogin();
    return (
      <Container text className="loginPanel">
        <Dimmer.Dimmable as={Form} onSubmit={this._onSubmit} size="large">
          <Dimmer
            role="progressbar"
            aria-describedby={intl.formatMessage(messages.loggingIn)}
            aria-busy={requesting}
            inverted
            active={requesting}
          >
            <Loader active={requesting} />
          </Dimmer>
          <Segment>
            <Form.Field>
              <label htmlFor="username">
                <FormattedMessage {...messages.username} />
              </label>
              <Input type="email" name="username" value={username} icon iconPosition="left" onChange={this._onChange}>
                <Icon name="mail" />
                <input
                  id="username"
                  autoComplete="off"
                  autoCapitalize="off"
                  aria-required
                  aria-invalid={!!missingName}
                />
              </Input>
              {missingName}
            </Form.Field>
            <Form.Field>
              <label htmlFor="password">
                <FormattedMessage {...messages.password} />
              </label>
              <Input type="password" name="password" value={password} icon iconPosition="left" onChange={this._onChange}>
                <Icon name="lock" />
                <input id="password" autoComplete="off" aria-required aria-invalid={!!missingPassword} />
              </Input>
              {missingPassword}
            </Form.Field>
            <Button primary type="submit" data-cy="login-submit-button">
              <FormattedMessage id="login-page.loginButton" defaultMessage="Login" />
            </Button>
          </Segment>
        </Dimmer.Dimmable>
        {errorDetails}
        <List as="dl" horizontal styleName="links">
          <List.Item as="dt" styleName="linkLeft">
            <a href={process.env.REGISTRATION_URL} data-cy="login-new-user-link">
              <FormattedMessage id="hs-registration.new-user" defaultMessage="New User?" />
            </a>
          </List.Item>
          <List.Item as="dt" styleName="linkCenter">
            {!_isMockingNavianceLogin && (
            <Button styleName="asLinkBtn" onClick={this._onNavianceLogin}>
              <FormattedMessage id="hs-login.naviance-user" defaultMessage="Naviance User?" />
            </Button>
            )}
            {_isMockingNavianceLogin && (
            <Link to="/mock-naviance-login" styleName="asLinkBtn" data-cy="login-mock-naviance-user-link">
              <FormattedMessage id="hs-login.mock-naviance-user" defaultMessage="Mock Naviance User?" />
            </Link>
            )}
          </List.Item>
          <List.Item as="dt" styleName="linkRight">
            <Link to="/forgot-password" data-cy="login-forgot-password-link">
              <FormattedMessage id="hs-registration.forgotPassword" defaultMessage="Forgot Password" />
            </Link>
          </List.Item>
        </List>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export const LoginPageComponent = injectIntl(LoginPage);

export default connect(mapStateToProps, { login, loginNaviance })(LoginPageComponent);
